import { z } from 'zod';
import {
  RequestFileSchema,
  StorageDownloadUrlResponseSchema,
  type RequestFile,
  type RequestFileCreateInput,
  type StorageDownloadUrlResponse,
} from '@magimp/shared';
import { request } from '../client.js';

// WHY (#1445): the list endpoint returns a bare array of the read wire
//   shape (objectKey omitted). Build the array parser here from the
//   shared row schema rather than re-declaring the row fields.
const RequestFileListSchema = z.array(RequestFileSchema);

/**
 * @aiPurpose `GET /requests/:id/files` — live (non-soft-deleted) file
 *   rows for the request, ordered by createdAt ascending.
 */
export function listRequestFiles(requestId: string): Promise<RequestFile[]> {
  return request<RequestFile[]>(
    `/requests/${encodeURIComponent(requestId)}/files`,
    { method: 'GET' },
    {},
    RequestFileListSchema,
  );
}

/**
 * @aiPurpose `POST /requests/:id/files` — record the metadata row that
 *   binds an already-PUT `request-attachment/...` object key to the
 *   request. The bytes were uploaded via the presigned URL beforehand.
 */
export function createRequestFile(
  requestId: string,
  input: RequestFileCreateInput,
): Promise<RequestFile> {
  return request<RequestFile>(
    `/requests/${encodeURIComponent(requestId)}/files`,
    {
      method: 'POST',
      body: JSON.stringify(input),
      headers: { 'content-type': 'application/json' },
    },
    {},
    RequestFileSchema,
  );
}

/**
 * @aiPurpose `DELETE /requests/:id/files/:fileId` — soft-delete a file.
 *   Idempotent: a second delete still resolves (204).
 */
export function deleteRequestFile(requestId: string, fileId: string): Promise<void> {
  return request<void>(
    `/requests/${encodeURIComponent(requestId)}/files/${encodeURIComponent(fileId)}`,
    { method: 'DELETE' },
    {},
    z.void(),
  );
}

/**
 * @aiPurpose `POST /requests/:id/files/:fileId/download-url` — mint a
 *   short-TTL presigned download URL for a live file. The raw storage
 *   key never leaves the backend; the server resolves it by file id.
 */
export function requestFileDownloadUrl(
  requestId: string,
  fileId: string,
): Promise<StorageDownloadUrlResponse> {
  // WHY: parameterless POST — the backend reads only the route params, so
  // the request carries no body. Sending `content-type: application/json`
  // with an empty body makes Fastify reject it (FST_ERR_CTP_EMPTY_JSON_BODY
  // → 500). Omit the header, mirroring `cargoPaymentDocumentDownloadUrl`.
  return request<StorageDownloadUrlResponse>(
    `/requests/${encodeURIComponent(requestId)}/files/${encodeURIComponent(fileId)}/download-url`,
    { method: 'POST' },
    {},
    StorageDownloadUrlResponseSchema,
  );
}
